/**
 * T15406 · Platform slot · queryPersister factory · web (idb-keyval).
 *
 * Builds a `QueryPersisterSlot` backed by TanStack's
 * `createAsyncStoragePersister`, with `idb-keyval` (or any injected
 * key-value pair API) as the underlying AsyncStorage shape.
 *
 * **Why IDB, not localStorage.** IDB persists across the storage
 * lifecycle events (freeze / pagehide) the way localStorage does, but
 * scales past localStorage's ~5–10 MB quota and survives Safari's
 * intelligent-tracking-prevention cleanup. The cache snapshot can grow
 * past 1 MB on a heavy-traffic session; localStorage would throw
 * QuotaExceededError under load.
 *
 * `flushSync()` writes a `last-sync` timestamp to `localStorage`
 * synchronously — this is the load-bearing surface for the RN
 * background-drain mirror in T1106 / T6006. The persister's actual
 * write is async (idb is non-blocking) but the timestamp gives
 * downstream code a sync read of "did we ever sync".
 */

import {
  recordPersisterQuotaExceeded,
  recordPersisterQuotaWarn,
} from '../../devtool/persister-quota.js';
import type { QueryPersisterSlot } from '../adapter.js';
import { createIdbAsyncStorage, type IdbKv } from './storage.js';

/** Soft warn threshold — the serialized cache crossing this is a devtool signal. */
const PERSISTER_QUOTA_WARN_BYTES = 4 * 1024 * 1024;

/** True for the cross-browser `QuotaExceededError` shapes. */
function isQuotaExceededError(e: unknown): boolean {
  return (
    e instanceof Error &&
    (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

export const WEB_PERSISTER_KEY = 'morfi.tanstack-cache';
export const WEB_PERSISTER_LAST_SYNC_KEY = `${WEB_PERSISTER_KEY}.last-sync`;

/**
 * Minimal `createAsyncStoragePersister` surface. The production wiring
 * imports the real implementation from
 * `@tanstack/query-async-storage-persister`; tests inject a stub.
 */
export interface AsyncStorageLike {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export interface CreateAsyncStoragePersisterOpts {
  storage: AsyncStorageLike;
  key?: string;
  throttleTime?: number;
}

export interface PersisterImpl {
  persistClient(client: unknown): Promise<void> | void;
  restoreClient(): Promise<unknown | undefined>;
  removeClient(): Promise<void> | void;
}

export type CreateAsyncStoragePersister = (opts: CreateAsyncStoragePersisterOpts) => PersisterImpl;

export interface WebQueryPersisterDeps {
  readonly idb: IdbKv;
  readonly createAsyncStoragePersister: CreateAsyncStoragePersister;
  readonly localStorage?: Pick<Storage, 'setItem'>;
  readonly now?: () => number;
  /**
   * Storage-key override. The web SDK provider passes a NETWORK-scoped key
   * (e.g. `morfi.tanstack-cache:devnet`) so each network keeps an isolated
   * persisted cache — without it, switching networks restores the previous
   * network's data into the fresh QueryClient. Defaults to the shared
   * {@link WEB_PERSISTER_KEY}.
   */
  readonly key?: string;
}

export function createWebQueryPersisterSlot(deps: WebQueryPersisterDeps): QueryPersisterSlot {
  const baseStorage = createIdbAsyncStorage(deps.idb);

  // Devtool quota instrumentation. The whole non-prod branch is
  // build-time DCE'd in production (`process.env.NODE_ENV` folds to a
  // constant) — the `recordPersisterQuota*` imports then go dead and
  // tree-shake away, zero production cost. In dev it measures every
  // persist write and records soft warns (serialized cache > 4 MB) +
  // hard `QuotaExceededError`s into the `persister.quota` devtool slice.
  const storage: AsyncStorageLike =
    process.env.NODE_ENV === 'production'
      ? baseStorage
      : {
          getItem: (k): Promise<string | null> => baseStorage.getItem(k),
          removeItem: (k): Promise<void> => baseStorage.removeItem(k),
          async setItem(k, value): Promise<void> {
            const sizeBytes = new Blob([value]).size;
            if (sizeBytes > PERSISTER_QUOTA_WARN_BYTES) recordPersisterQuotaWarn(sizeBytes);
            try {
              await baseStorage.setItem(k, value);
            } catch (e) {
              if (isQuotaExceededError(e)) recordPersisterQuotaExceeded(sizeBytes);
              throw e;
            }
          },
        };

  const persisterKey = deps.key ?? WEB_PERSISTER_KEY;
  const lastSyncKey = `${persisterKey}.last-sync`;
  const persister = deps.createAsyncStoragePersister({
    storage,
    key: persisterKey,
  });
  const now = deps.now ?? ((): number => Date.now());
  const ls =
    deps.localStorage ??
    (typeof globalThis !== 'undefined' &&
    'localStorage' in globalThis &&
    (globalThis as { localStorage?: unknown }).localStorage !== undefined
      ? (globalThis as { localStorage: Pick<Storage, 'setItem'> }).localStorage
      : undefined);

  return {
    // Cast at the boundary: the structural shape is identical to
    // TanStack's `Persister` (T1101 declares the structural type).
    persister: persister as QueryPersisterSlot['persister'],
    flushSync(): void {
      if (ls === undefined) return;
      try {
        ls.setItem(lastSyncKey, String(now()));
      } catch {
        // private mode / quota / SecurityError — swallow; flushSync
        // is best-effort by contract.
      }
    },
  };
}
